import { Box, IconButton, Stack, Typography, styled } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FileText, FolderOpen, Upload, X } from "lucide-react";
import { filesize } from "filesize";
import { DocumentPickerValue } from "./DocumentPicker.types";

interface SelectedDocumentCardProps {
    value: DocumentPickerValue;
    onRemove: () => void;
    onChange: (event: React.MouseEvent<HTMLElement>) => void;
}

const Card = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    transition: "border-color 0.15s ease",
    "&:hover": {
        borderColor: theme.palette.grey[300],
    },
}));

const SourceIcon = styled(Box, {
    shouldForwardProp: (prop) => prop !== "source",
})<{ source: "upload" | "library" }>(({ theme, source }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 8,
    flexShrink: 0,
    backgroundColor:
        source === "library"
            ? alpha(theme.palette.primary.main, 0.08)
            : alpha(theme.palette.success.main, 0.08),
    color:
        source === "library"
            ? theme.palette.primary.main
            : theme.palette.success.main,
}));

export const SelectedDocumentCard = ({
    value,
    onRemove,
    onChange,
}: SelectedDocumentCardProps) => {
    const Icon = value.source === "library" ? FolderOpen : Upload;

    const metaParts: string[] = [];
    if (value.source === "upload") {
        metaParts.push("Uploaded");
        if (value.byteSize) {
            metaParts.push(filesize(value.byteSize, { round: 1 }) as string);
        }
    } else {
        metaParts.push("From Library");
        if (value.folderName) {
            metaParts.push(value.folderName);
        }
    }

    return (
        <Card>
            <SourceIcon source={value.source}>
                <Icon size={18} strokeWidth={2} />
            </SourceIcon>

            <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <FileText
                        size={14}
                        style={{ flexShrink: 0, opacity: 0.5 }}
                    />
                    <Typography
                        noWrap
                        sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color: "text.primary",
                        }}
                    >
                        {value.title}
                    </Typography>
                </Stack>
                <Typography
                    noWrap
                    sx={{
                        fontSize: "0.7rem",
                        color: "text.disabled",
                        mt: 0.25,
                    }}
                >
                    {metaParts.join(" · ")}
                </Typography>
            </Stack>

            <Typography
                component="button"
                onClick={onChange}
                sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: "primary.main",
                    cursor: "pointer",
                    border: "none",
                    background: "none",
                    borderRadius: "4px",
                    px: 0.75,
                    py: 0.25,
                    flexShrink: 0,
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                        backgroundColor: (theme) =>
                            alpha(theme.palette.primary.main, 0.06),
                    },
                    "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "primary.main",
                        outlineOffset: 1,
                    },
                }}
            >
                Change
            </Typography>

            <IconButton
                size="small"
                onClick={onRemove}
                aria-label="Remove document"
                sx={{
                    p: 0.25,
                    color: "text.disabled",
                    "&:hover": {
                        color: "error.main",
                        backgroundColor: (theme) =>
                            alpha(theme.palette.error.main, 0.08),
                    },
                }}
            >
                <X size={16} />
            </IconButton>
        </Card>
    );
};
